import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {DbDate} from "../db-date";
import {DbDateStruct} from "../date-struct";
import {PlacementArray} from "../../util/positioning";
import {IDatepickerNavigateEvent} from "../datepicker";
import {DbDateParserFormatter} from "../ngb-date-parser-formatter";
import {DatePipe} from "@angular/common";

@Component({
    selector: 'db-datepicker-select',
    templateUrl: './datepicker-select.component.html',
    styleUrls: ['./datepicker-select.component.scss'],
    standalone: false
})
export class DatepickerSelectComponent implements OnChanges {

  @Input() public dsDatePickerWidth?: string = '100%';
  @Input() public dsDatePickerHeight?: string = '40px';
  @Input() autoClose?: boolean | 'inside' | 'outside' = 'outside';

  /** The number of months to display.*/
  @Input() displayMonths?: number = 1;

  /** The first day of the week.*/
  @Input() firstDayOfWeek?: number = 0;

  /**
   * The callback to mark some dates as disabled.
   *
   * It is called for each new date when navigating to a different month.
   *
   * `current` is the month that is currently displayed by the datepicker.
   */
  @Input() markDisabled?: (date: DbDate, current?: {year: number, month: number}) => boolean;

  /**
   * The earliest date that can be displayed or selected. Also used for form validation.
   *
   * If not provided, 'year' select box will display 10 years before the current month.
   */
  @Input() minDate?: DbDateStruct;

  /**
   * The latest date that can be displayed or selected. Also used for form validation.
   *
   * If not provided, 'year' select box will display 10 years after the current month.
   */
  @Input() maxDate?: DbDateStruct;

  /**
   * The way of displaying days that don't belong to the current month.
   *
   * * `"visible"` - days are visible
   * * `"hidden"` - days are hidden, white space preserved
   * * `"collapsed"` - days are collapsed, so the datepicker height might change between months
   *
   * For the 2+ months view, days in between months are never shown.
   */
  @Input() outsideDays?: 'visible' | 'collapsed' | 'hidden' = 'visible';

  /**
   * The preferred placement of the datepicker popup.
   *
   * Possible values are `"top"`, `"top-left"`, `"top-right"`, `"bottom"`, `"bottom-left"`,
   * `"bottom-right"`, `"left"`, `"left-top"`, `"left-bottom"`, `"right"`, `"right-top"`,
   * `"right-bottom"`
   *
   * Accepts an array of strings or a string with space separated possible values.
   *
   * The default order of preference is `"bottom-left bottom-right top-left top-right"`
   *
   * Please see the [positioning overview](#/positioning) for more details.
   */
  @Input() placement?: PlacementArray = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];

  /**
   * The date to open calendar with.
   *
   * With the default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date is provided, calendar will open with current month.
   *
   * You could use `navigateTo(date)` method as an alternative.
   */
  @Input() startDate?: DbDateStruct;

  /** The last selected date to open calendar with.*/
  @Input() endDate?: DbDateStruct | null;

  @Input() canSelectRange?: boolean = false;

  /** Emit dateSelect() on every date selection.*/
  @Input() selectOnClick?: boolean = false;

  @Input() disabled?: boolean = false;

  /**
   * An event emitted when user selects a date using keyboard or mouse.
   *
   * The payload of the event is currently selected `DbDate`.*/
  @Output() dateSelect = new EventEmitter<[DbDate, DbDate]>();

  /**
   * An event emitted when user press Clear button.
   *
   * The payload of the event is void.
   */
  @Output() dateClear = new EventEmitter<void>();

  /**
   * Event emitted right after the navigation happens and displayed month changes.
   *
   * See [`IDatepickerNavigateEvent`](#/components/datepicker/api#IDatepickerNavigateEvent) for the payload info.
   */
  @Output() navigate = new EventEmitter<IDatepickerNavigateEvent>();

  /** An event fired after closing datepicker window.*/
  @Output() closed = new EventEmitter<void>();

  public toggled: boolean = false;
  public datePlaceHolder?: string = 'Date';

  constructor(
    private _parseFormatter: DbDateParserFormatter,
    private _datePipe: DatePipe
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startDate'] || changes['endDate']) {
      let date = null;
      if (changes['startDate'].currentValue) {
        date = [changes['startDate'].currentValue, changes['endDate'].currentValue];
      }
      this._setDatePlaceHolder(date);
    }
  }

  public onDateSelect(date): void {
    this._setDatePlaceHolder(date);
    this.dateSelect.emit(date);
  }

  public onDateClear(): void {
    this._setDatePlaceHolder();
    this.dateClear.emit();
  }

  public close(): void {
    this.closed.emit();
    this.toggled = false;
  }

  private _setDatePlaceHolder(date: [DbDate, DbDate] = null) {
    if (!date) {
      this.datePlaceHolder = 'Date';
      return;
    }
    const [start, end] = date;
    const startDate = this._parseFormatter.format(start);
    this.datePlaceHolder = this._datePipe.transform(new Date(startDate).getTime(), 'd MMM yy');
    if (end) {
      const endDate = this._parseFormatter.format(end);
      this.datePlaceHolder = `${this.datePlaceHolder} - ${this._datePipe.transform(new Date(endDate).getTime(), 'd MMM yy')}`;
    }
  }
}
